import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { FaThumbsUp, FaThumbsDown, FaRegCommentDots, FaShare } from 'react-icons/fa'
import * as normalActions from '../store/actions'
import { useAuthGate } from './authPrompt'
import { firstPostImage, parsePostImages, uploadUrl } from '../utils/postImages'
import { isShortPost, isWatchVideo, leavePath, shareMedia, watchClipPath } from '../utils/mediaRoute'
import { AUDIO_FILE, findSoundSource, parseSoundId, soundIdOf, visibleTags } from '../utils/sound'
import UserAvatar from './userAvatar'
import WatchMoreMenu from './watchMoreMenu'

const VOTES_KEY = 'pinweelo-votes'
const DESC_PREVIEW = 92

const formatViews = views => {
  const n = Number(views) || 0
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`
  return String(n)
}

const readVotes = () => {
  try {
    return JSON.parse(localStorage.getItem(VOTES_KEY) || '{}')
  } catch (err) {
    return {}
  }
}

const voteKey = (userId, postId, type) => `${userId}:${postId}:${type}`

const getSavedVote = (userId, postId, type) => {
  if (!userId || !postId) return false
  return Boolean(readVotes()[voteKey(userId, postId, type)])
}

const WatchItem = ({ item, active, nearby, onOpenComments, onImmersive, onHide }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { userid: viewerId, requireAuth, promptEl } = useAuthGate()
  const upds = useSelector(state => state.UpdsReduse.upds || [])
  const voteDelta = useSelector(state => (state.UpdsReduse.voteDelta || {})[String(item.id)] || { p: 0, d: 0 })
  const followList = useSelector(state => state.followReduse.follow || [])
  const shorts = useSelector(state => state.PostReducer.followShort || [])
  const longs = useSelector(state => state.PostReducer.followLong || [])
  const userPosts = useSelector(state => state.PostReducer.userPosts || [])
  const postId = item.id
  const photoNames = parsePostImages(item.image)
  const [photoIndex, setPhotoIndex] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [buffering, setBuffering] = useState(false)
  const [hasFrame, setHasFrame] = useState(false)
  const [showPlayIcon, setShowPlayIcon] = useState(false)
  const [iconPaused, setIconPaused] = useState(false)
  const [descOpen, setDescOpen] = useState(false)
  const videoRef = useRef(null)
  const soundRef = useRef(null)
  const fillRef = useRef(null)
  const trackRef = useRef(null)
  const knobRef = useRef(null)
  const timelineRef = useRef(null)
  const scrubbing = useRef(false)
  const seekRaf = useRef(0)
  const pendingTime = useRef(null)
  const playIconTimer = useRef(null)
  const holdRef = useRef({ timer: 0, held: false, x: 0, y: 0 })
  const [barOpen, setBarOpen] = useState(false)
  const [barBox, setBarBox] = useState(null)
  const isPhoto = !isWatchVideo(item)
  const isShort = isShortPost(item)
  const attachedSoundId = parseSoundId(item.tages)
  const discSoundId = soundIdOf(item)
  const soundSource = attachedSoundId ? findSoundSource(attachedSoundId, [shorts, longs, userPosts, [item]]) : item
  const soundFile = soundSource && soundSource.image
  const useAttachedAudio = Boolean(attachedSoundId && soundFile && attachedSoundId !== String(item.id))
  const isFollowing = followList.some(entry => String(entry.userid) === String(item.userid))
  const isOwn = viewerId && String(viewerId) === String(item.userid)

  const likedFromStore = Boolean(upds.find(entry => String(entry.userid) === String(viewerId) && String(entry.postid) === String(postId) && entry.type === 'p'))
  const dislikedFromStore = Boolean(upds.find(entry => String(entry.userid) === String(viewerId) && String(entry.postid) === String(postId) && entry.type === 'd'))
  const liked = likedFromStore || getSavedVote(viewerId, postId, 'p')
  const disliked = dislikedFromStore || getSavedVote(viewerId, postId, 'd')
  const likeCount = Math.max(0, Number(item.vlikes || 0) + (Number(voteDelta.p) || 0))
  const dislikeCount = Math.max(0, Number(item.vdislikes || 0) + (Number(voteDelta.d) || 0))
  const description = item.description || 'Tap to like and swipe up to see the next clip.'
  const descOverflow = description.length > DESC_PREVIEW
  const descShown = descOpen || !descOverflow
    ? description
    : `${description.slice(0, DESC_PREVIEW).replace(/\s+\S*$/, '').trimEnd()}...`

  useLayoutEffect(() => {
    if (!active || isPhoto) {
      setBarBox(null)
      return undefined
    }
    const header = document.querySelector('.fresh-header')
    if (!header) return undefined
    const sync = () => {
      const rect = header.getBoundingClientRect()
      setBarBox({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      })
    }
    sync()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync)
    if (observer) observer.observe(header)
    window.addEventListener('resize', sync)
    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('resize', sync)
    }
  }, [active, isPhoto])

  useEffect(() => {
    return () => {
      if (playIconTimer.current) window.clearTimeout(playIconTimer.current)
      window.clearTimeout(holdRef.current.timer)
      if (seekRaf.current) window.cancelAnimationFrame(seekRaf.current)
    }
  }, [])

  useEffect(() => {
    if (!active || isPhoto) {
      setDescOpen(false)
      setBarOpen(false)
      scrubbing.current = false
    }
  }, [active, postId])

  useEffect(() => {
    if (!active || !postId || isPhoto) return undefined
    dispatch(normalActions.add_to_views(viewerId || 'guest', postId))
    return undefined
  }, [active, postId, isPhoto, viewerId, dispatch])

  useEffect(() => {
    const video = videoRef.current
    if (!active || !barBox || !video || !video.duration) return undefined
    applyFill((video.currentTime / video.duration) * 100)
    return undefined
  }, [active, barBox, barOpen])

  useEffect(() => {
    setHasFrame(false)
  }, [item.image, postId])

  useEffect(() => {
    const video = videoRef.current
    const extra = soundRef.current
    if (!video || isPhoto) return undefined
    if (!nearby) {
      video.pause()
      if (extra) extra.pause()
      setPlaying(false)
      setBuffering(false)
      return undefined
    }
    if (active) {
      if (video.readyState >= 2) setHasFrame(true)
      else setBuffering(true)
      video.play().then(() => setPlaying(true)).catch(() => setPlaying(false))
      extra && extra.play().catch(() => {})
    } else {
      video.pause()
      if (extra) extra.pause()
      setPlaying(false)
      setBuffering(false)
    }
    return undefined
  }, [active, nearby, item.image, isPhoto, useAttachedAudio, soundFile])

  useEffect(() => {
    const video = videoRef.current
    if (!video || isPhoto) return undefined

    const syncBuffering = () => {
      if (!active) {
        setBuffering(false)
        return
      }
      const needsData = video.readyState < 2 || video.seeking
      setBuffering(Boolean(needsData && !video.paused))
    }

    const onWaiting = () => { if (active && !video.paused) setBuffering(true) }
    const onStalled = () => { if (active && !video.paused) setBuffering(true) }
    const onSeeking = () => { if (active && video.readyState < 2) setBuffering(true) }
    const markFrame = () => { if (video.readyState >= 2) setHasFrame(true) }
    const onPlaying = () => { markFrame(); setBuffering(false) }
    const onCanPlay = () => { markFrame(); if (!video.seeking && !video.paused) setBuffering(false) }
    const onLoadedData = () => { markFrame(); if (video.readyState >= 2 && !video.seeking) setBuffering(false) }
    const onError = () => setBuffering(false)

    video.addEventListener('waiting', onWaiting)
    video.addEventListener('stalled', onStalled)
    video.addEventListener('seeking', onSeeking)
    video.addEventListener('seeked', syncBuffering)
    video.addEventListener('playing', onPlaying)
    video.addEventListener('canplay', onCanPlay)
    video.addEventListener('canplaythrough', onCanPlay)
    video.addEventListener('loadeddata', onLoadedData)
    video.addEventListener('error', onError)
    syncBuffering()

    return () => {
      video.removeEventListener('waiting', onWaiting)
      video.removeEventListener('stalled', onStalled)
      video.removeEventListener('seeking', onSeeking)
      video.removeEventListener('seeked', syncBuffering)
      video.removeEventListener('playing', onPlaying)
      video.removeEventListener('canplay', onCanPlay)
      video.removeEventListener('canplaythrough', onCanPlay)
      video.removeEventListener('loadeddata', onLoadedData)
      video.removeEventListener('error', onError)
    }
  }, [active, nearby, item.image, isPhoto])

  const flashPlayIcon = () => {
    setShowPlayIcon(true)
    if (playIconTimer.current) window.clearTimeout(playIconTimer.current)
    playIconTimer.current = window.setTimeout(() => setShowPlayIcon(false), 700)
  }

  const clearHold = () => {
    window.clearTimeout(holdRef.current.timer)
    holdRef.current.timer = 0
  }

  const beginHold = event => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    clearHold()
    holdRef.current.held = false
    holdRef.current.x = event.clientX
    holdRef.current.y = event.clientY
    holdRef.current.timer = window.setTimeout(() => {
      holdRef.current.held = true
      holdRef.current.timer = 0
      if (onImmersive) onImmersive(true)
    }, 480)
  }

  const moveHold = event => {
    if (!holdRef.current.timer) return
    if (Math.abs(event.clientX - holdRef.current.x) > 14 || Math.abs(event.clientY - holdRef.current.y) > 14) {
      clearHold()
    }
  }

  const endHold = () => clearHold()

  const toggleShortPlay = event => {
    if (holdRef.current.held) {
      holdRef.current.held = false
      event.preventDefault()
      event.stopPropagation()
      return
    }
    event.preventDefault()
    event.stopPropagation()
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      setIconPaused(false)
      if (video.readyState < 2) setBuffering(true)
      video.play().then(() => {
        setPlaying(true)
        flashPlayIcon()
        soundRef.current && soundRef.current.play().catch(() => {})
      }).catch(() => setPlaying(false))
    } else {
      setIconPaused(true)
      video.pause()
      if (soundRef.current) soundRef.current.pause()
      setPlaying(false)
      flashPlayIcon()
    }
  }

  const handleLike = event => {
    event.preventDefault()
    event.stopPropagation()
    if (!requireAuth() || !postId) return
    dispatch(normalActions.add_to_upds('p', viewerId, postId))
  }

  const handleDislike = event => {
    event.preventDefault()
    event.stopPropagation()
    if (!requireAuth() || !postId) return
    dispatch(normalActions.add_to_upds('d', viewerId, postId))
  }

  const handleComments = event => {
    event.preventDefault()
    event.stopPropagation()
    if (!postId) return
    onOpenComments(postId)
  }

  const handleShare = async event => {
    event.preventDefault()
    event.stopPropagation()
    if (!postId) return
    const path = watchClipPath(postId)
    try {
      window.history.replaceState(window.history.state, '', path)
    } catch (_) {}
    await shareMedia(item, 'Pinweelo video', path)
  }

  const handleProfile = event => {
    event.preventDefault()
    event.stopPropagation()
    if (item.userid) navigate(`/@${item.userid}`)
  }

  const handleSubscribe = event => {
    event.preventDefault()
    event.stopPropagation()
    if (isOwn || !requireAuth() || !item.userid) return
    if (isFollowing) dispatch(normalActions.remove_follow(item.userid))
    else dispatch(normalActions.add_to_follow(item.userid, item.email))
  }

  const applyFill = pct => {
    const p = Math.min(100, Math.max(0, pct))
    if (fillRef.current) fillRef.current.style.strokeDasharray = `${p} 100`
    const path = trackRef.current
    if (path && knobRef.current && path.getTotalLength) {
      const len = path.getTotalLength()
      if (!len) return
      const point = path.getPointAtLength((p / 100) * len)
      knobRef.current.style.left = `${point.x}px`
      knobRef.current.style.top = `${point.y}px`
    }
  }

  const flushSeek = () => {
    seekRaf.current = 0
    const video = videoRef.current
    if (!video || pendingTime.current == null) return
    video.currentTime = pendingTime.current
  }

  const seekShort = (event, commit) => {
    const video = videoRef.current
    const wrap = timelineRef.current
    if (!video || !video.duration || !wrap) return
    const rect = wrap.getBoundingClientRect()
    const x = (event.clientX ?? event.changedTouches?.[0]?.clientX ?? 0) - rect.left
    const next = Math.min(video.duration, Math.max(0, (x / Math.max(rect.width, 1)) * video.duration))
    pendingTime.current = next
    applyFill((next / video.duration) * 100)
    if (commit) {
      if (seekRaf.current) window.cancelAnimationFrame(seekRaf.current)
      seekRaf.current = 0
      video.currentTime = next
      return
    }
    if (!seekRaf.current) seekRaf.current = window.requestAnimationFrame(flushSeek)
  }

  const onTimelineDown = event => {
    event.stopPropagation()
    event.preventDefault()
    scrubbing.current = true
    setBarOpen(true)
    try { event.currentTarget.setPointerCapture(event.pointerId) } catch (err) {}
    seekShort(event, true)
  }

  const onTimelineMove = event => {
    if (!scrubbing.current) return
    event.stopPropagation()
    seekShort(event)
  }

  const onTimelineUp = event => {
    if (!scrubbing.current) return
    event.stopPropagation()
    seekShort(event, true)
    scrubbing.current = false
    window.setTimeout(() => { if (!scrubbing.current) setBarOpen(false) }, 420)
  }

  const updateFill = event => {
    if (scrubbing.current) return
    const video = event.currentTarget
    if (!video.duration) return
    applyFill((video.currentTime / video.duration) * 100)
  }

  const stroke = barOpen ? 7 : 3
  const pad = stroke + 8
  const radius = 32
  const pathD = barBox ? (() => {
    const w = barBox.width
    const r = Math.min(radius, w / 2, barBox.height / 2)
    const R = r + stroke / 2
    const left = pad
    const top = pad
    return [
      `M ${left - stroke / 2} ${top + r}`,
      `A ${R} ${R} 0 0 1 ${left + r} ${top - stroke / 2}`,
      `H ${left + w - r}`,
      `A ${R} ${R} 0 0 1 ${left + w + stroke / 2} ${top + r}`
    ].join(' ')
  })() : ''
  const svgW = barBox ? barBox.width + pad * 2 : 0
  const svgH = pad + radius + stroke + 10

  return (
    <article className={`watchItem${isShort ? ' isShort' : ' isPost'}${active ? ' isActive' : ''}`}>
      {promptEl}
      {isPhoto ? (
        <div
          className="watchItemCarousel"
          onPointerDown={beginHold}
          onPointerMove={moveHold}
          onPointerUp={endHold}
          onPointerCancel={endHold}
          onContextMenu={event => event.preventDefault()}
          onTouchStart={event => { event.currentTarget.dataset.x = event.changedTouches[0].clientX }}
          onTouchEnd={event => {
            const start = Number(event.currentTarget.dataset.x || 0)
            const dx = event.changedTouches[0].clientX - start
            if (photoNames.length < 2) return
            if (dx > 40) setPhotoIndex((photoIndex - 1 + photoNames.length) % photoNames.length)
            if (dx < -40) setPhotoIndex((photoIndex + 1) % photoNames.length)
          }}
        >
          <img
            className="watchItemMedia"
            src={nearby ? uploadUrl(photoNames[photoIndex] || firstPostImage(item.image) || item.image) : undefined}
            alt={item.title || item.email}
          />
          {photoNames.length > 1 && (
            <div className="watchItemDots">
              {photoNames.map((_, i) => (
                <span key={i} className={i === photoIndex ? 'isActive' : ''} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div
          className="watchStage"
          onClick={toggleShortPlay}
          onPointerDown={beginHold}
          onPointerMove={moveHold}
          onPointerUp={endHold}
          onPointerCancel={endHold}
          onContextMenu={event => event.preventDefault()}
        >
          <video
            ref={videoRef}
            className="watchItemMedia"
            src={nearby ? uploadUrl(item.image) : undefined}
            muted={useAttachedAudio}
            playsInline
            loop
            preload={active ? 'auto' : 'metadata'}
            poster={nearby && item.thumbnail ? uploadUrl(item.thumbnail) : undefined}
            onPlay={() => setPlaying(true)}
            onPause={() => { setPlaying(false); setBuffering(false) }}
            onTimeUpdate={updateFill}
          />
          {isShort && nearby && item.thumbnail && !hasFrame && (
            <img className="watchItemPoster" src={uploadUrl(item.thumbnail)} alt="" />
          )}
          {isShort && buffering && active && (
            <div className="watchBufferSpinner" role="status" aria-label="Loading video" />
          )}
          {useAttachedAudio && nearby && (AUDIO_FILE.test(String(soundFile || '')) ? (
            <audio
              ref={soundRef}
              className="watchSoundAudio"
              src={uploadUrl(soundFile)}
              loop
              preload={active ? 'auto' : 'metadata'}
            />
          ) : useAttachedAudio && nearby ? (
            <video
              ref={soundRef}
              className="watchSoundAudio"
              src={uploadUrl(soundFile)}
              loop
              playsInline
              preload={active ? 'auto' : 'metadata'}
            />
          ) : null)}
        </div>
      )}

      <div className="watchHud">
        <div className="watchItemOverlay" />

        {!isPhoto && (
          <button
            type="button"
            className={`watchPlayButton${iconPaused ? ' isPlaying' : ''}${showPlayIcon && !buffering ? ' isOn' : ''}`}
            aria-hidden="true"
            tabIndex={-1}
          />
        )}

        <div className="watchActions" onClick={event => event.stopPropagation()}>
          <button type="button" className={`watchAction${liked ? ' isActive' : ''}`} onClick={handleLike} aria-pressed={liked}>
            <FaThumbsUp />
            <span>{likeCount}</span>
          </button>
          <button type="button" className={`watchAction isDislike${disliked ? ' isActive' : ''}`} onClick={handleDislike} aria-pressed={disliked}>
            <FaThumbsDown />
            <span>{dislikeCount}</span>
          </button>
          <button type="button" className="watchAction" onClick={handleComments} aria-label="Comments">
            <FaRegCommentDots />
            <span>{item.vcomments || 0}</span>
          </button>
          <button type="button" className="watchAction" onClick={handleShare}>
            <FaShare />
            <span>Share</span>
          </button>
          <WatchMoreMenu
            item={item}
            onHide={onHide}
            onImmersive={onImmersive}
            onContent={() => setDescOpen(true)}
          />
          <button
            type="button"
            className={`watchMusicDisc${playing ? ' isSpinning' : ''}`}
            aria-label="Open shorts with this sound"
            onPointerDown={event => event.stopPropagation()}
            onClick={event => {
              event.preventDefault()
              event.stopPropagation()
              const id = discSoundId || String(item.id || '')
              if (!id) return
              navigate(`/sound/${encodeURIComponent(id)}`, { state: { item, from: leavePath(location) } })
            }}
          >
            {(soundSource && soundSource.thumbnail) || item.thumbnail ? (
              <img src={`/api/uploads/${(soundSource && soundSource.thumbnail) || item.thumbnail}`} alt="" />
            ) : null}
          </button>
        </div>

        <div className={`watchItemText${descOpen ? ' isOpen' : ''}`}>
          <div className="watchItemAuthor">
            <UserAvatar src={item.userImg} name={item.email} alt={item.email} userId={item.userid} onClick={handleProfile} />
            <div>
              <div className="watchItemAuthorRow">
                <strong onClick={handleProfile}>@{item.email || 'unknown'}</strong>
                {!isOwn && (
                  <button
                    type="button"
                    className={`watchSubscribe${isFollowing ? ' isOn' : ''}`}
                    onClick={handleSubscribe}
                  >
                    {isFollowing ? 'Subscribed' : 'Subscribe'}
                  </button>
                )}
              </div>
              <span>{formatViews(item.views)} views</span>
            </div>
          </div>

          <p className={`watchItemDescription${descOpen ? ' isOpen' : ''}`}>
            {descShown}
            {descOverflow && (
              <>
                {' '}
                <button
                  type="button"
                  className="watchMore"
                  onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    setDescOpen(open => !open)
                  }}
                >
                  {descOpen ? 'less' : 'more'}
                </button>
              </>
            )}
          </p>

          <div className="watchItemHashtags">
            {visibleTags(item.tages) ? visibleTags(item.tages).split(',').map((tag, index) => (
              <span key={`${tag}-${index}`}>#{tag.trim()}</span>
            )) : <span>#trend</span>}
          </div>
        </div>

        {!isPhoto && active && barBox && createPortal(
          <div
            ref={timelineRef}
            className={`watchTimelineWrap${barOpen ? ' isOpen' : ''}`}
            style={{
              left: barBox.left - pad,
              top: barBox.top - pad,
              width: svgW,
              height: pad + 24
            }}
            onClick={event => event.stopPropagation()}
            onPointerDown={onTimelineDown}
            onPointerMove={onTimelineMove}
            onPointerUp={onTimelineUp}
            onPointerCancel={onTimelineUp}
            role="slider"
            aria-label="Video timeline"
          >
            <svg
              className="watchTimelineSvg"
              width={svgW}
              height={svgH}
              viewBox={`0 0 ${svgW} ${svgH}`}
            >
              <path
                ref={trackRef}
                className="watchTimelineTrack"
                d={pathD}
                fill="none"
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                ref={fillRef}
                className="watchTimelineFill"
                d={pathD}
                fill="none"
                pathLength="100"
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div ref={knobRef} className="watchTimelineKnob" />
          </div>,
          document.body
        )}
      </div>
    </article>
  )
}

export default React.memo(WatchItem)
